// services/zeno.service.js
import assistantClient, { handleApiError } from '../api/assistantClient';

/**
 * Servicio para interactuar con Zeno Bot
 * Zeno es el asistente especializado de Zenit AI para workflows
 */
class ZenoService {
  /**
   * Obtener información de Zeno y verificar disponibilidad
   */
  async getZenoInfo() {
    try {
      const response = await assistantClient.get('/zeno');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Verificar salud de Zeno
   */
  async getZenoHealth() {
    try {
      const response = await assistantClient.get('/zeno/health');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener capacidades de Zeno
   */
  async getZenoCapabilities() {
    try {
      const response = await assistantClient.get('/zeno/capabilities');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Chat con Zeno - NUEVO ENDPOINT
   * POST /accounts/{account_id}/zeno/chat
   * Body: { "message": "Tu mensaje", "use_memory": true }
   * Response: { "chatHistoryId": "id", "response": "respuesta", ... }
   */
  async chatWithZeno({ 
    accountId,
    message, 
    chatHistoryId = null,
    context = null,
    workflowContext = null, 
    useMemory = true 
  }) {
    try {
      // Validar parámetros requeridos
      if (!accountId) {
        return { 
          success: false, 
          error: { message: 'Account ID is required' }
        };
      }

      if (!message || !message.trim()) {
        return { 
          success: false, 
          error: { message: 'Message is required' }
        };
      }

      // Construir payload según nueva API
      const payload = {
        message: message.trim(),
        use_memory: useMemory,
      };

      // Agregar chatHistoryId si se proporciona (para continuar conversación)
      if (chatHistoryId) {
        payload.chat_history_id = chatHistoryId;
      }

      // Agregar campos opcionales si se proporcionan
      if (context) {
        payload.context = context;
      }

      if (workflowContext) {
        payload.workflowContext = workflowContext;
      }

      console.log('🤖 Sending message to Zeno:', { accountId, payload });

      // Usar NUEVO endpoint con account_id
      const response = await assistantClient.post(`/accounts/${accountId}/zeno/chat`, payload);
      console.log('🤖 Zeno response:', response.data);
      
      return { 
        success: true, 
        data: {
          messageId: response.data.messageId,
          chatHistoryId: response.data.chatHistoryId,
          response: response.data.response,
          workflowSuggestion: response.data.workflowSuggestion,
          nextActions: response.data.nextActions,
          metadata: response.data.metadata
        }
      };
    } catch (error) {
      console.error('Error chatting with Zeno:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener sugerencia de workflow de Zeno
   */
  async suggestWorkflow({ userId, workflowDescription, context, accountId }) {
    try {
      const payload = {
        user_id: userId,
        workflow_description: workflowDescription,
      };

      if (context) {
        payload.context = context;
      }

      if (accountId) {
        payload.account_id = accountId;
      }

      const response = await assistantClient.post('/zeno/workflows/suggest', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener un chat history específico
   * GET /chat/histories/{chat_history_id}
   */
  async getChatHistory(chatHistoryId) {
    try {
      if (!chatHistoryId) {
        return { 
          success: false, 
          error: { message: 'Chat History ID is required' }
        };
      }

      const response = await assistantClient.get(`/chat/histories/${chatHistoryId}`);
      
      // Intentar también obtener los mensajes si no vienen incluidos
      if (!response.data.messages || response.data.totalMessages === 0) {
        try {
          const messagesResponse = await assistantClient.get(`/chat/histories/${chatHistoryId}/messages`);
          response.data.messages = messagesResponse.data.messages || messagesResponse.data;
        } catch (messagesError) {
          // Si no funciona, intentar otro endpoint posible
          try {
            const altResponse = await assistantClient.get(`/chat/histories/${chatHistoryId}?include_messages=true`);
            response.data.messages = altResponse.data.messages || [];
          } catch (altError) {
            // Si ninguno funciona, devolver sin mensajes
            response.data.messages = [];
          }
        }
      }
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error getting chat history:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar todos los chats con Zeno para una cuenta
   * GET /accounts/{account_id}/chat/histories?assistant_id=00000000-0000-0000-0000-0000000000e0
   */
  async listZenoChats(accountId, { limit = 50, offset = 0 } = {}) {
    try {
      if (!accountId) {
        return { 
          success: false, 
          error: { message: 'Account ID is required' }
        };
      }

      // ID específico de Zeno según tu documentación
      const zenoAssistantId = '00000000-0000-0000-0000-0000000000e0';
      
      const response = await assistantClient.get(`/accounts/${accountId}/chat/histories`, {
        params: {
          assistant_id: zenoAssistantId,
          limit,
          offset
        }
      });

      // La respuesta tiene la estructura: { chatHistories: Array, total: number, limit: number, offset: number }
      return { 
        success: true, 
        data: response.data.chatHistories || [], // Extraer solo el array de chatHistories
        total: response.data.total || 0,
        limit: response.data.limit || limit,
        offset: response.data.offset || offset
      };
    } catch (error) {
      console.error('Error listing Zeno chats:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Cargar mensajes de un chat history específico
   * Útil para continuar una conversación existente
   */
  async loadChatMessages(chatHistoryId) {
    try {
      const result = await this.getChatHistory(chatHistoryId);
      
      if (result.success) {
        const messages = result.data.messages || [];
        
        // Transformar mensajes a formato compatible con el hook
        const transformedMessages = messages.map(msg => {
          let content = '';
          
          // Extraer contenido de contentBlocks si existe
          if (msg.contentBlocks && Array.isArray(msg.contentBlocks) && msg.contentBlocks.length > 0) {
            content = msg.contentBlocks.map(block => {
              if (block.data) {
                if (typeof block.data === 'string') return block.data;
                if (block.data.text) return block.data.text;
                if (Array.isArray(block.data)) return block.data.join(' ');
                return JSON.stringify(block.data);
              }
              return block.content || block.text || '';
            }).join('\n');
          } else {
            // Fallback a campos tradicionales
            content = msg.content || msg.message || msg.text || '';
          }
          
          return {
            id: msg.id || msg.messageId,
            role: msg.role === 'human' ? 'user' : (msg.role === 'assistant' ? 'assistant' : msg.role),
            message: content,
            content: content,
            contentBlocks: msg.contentBlocks, // Mantener original para el renderizado
            createdAt: msg.createdAt || msg.timestamp,
            metadata: msg.metadata
          };
        });
        
        return { 
          success: true, 
          data: {
            ...result.data,
            messages: transformedMessages
          }
        };
      }
      
      return result;
    } catch (error) {
      console.error('Error loading chat messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Crear memoria de Zeno para el usuario
   */
  async createZenoMemory({ userId, memoryContent, memoryCapacity = 2000 }) {
    try {
      const response = await assistantClient.post('/zeno-memory', {
        user_id: userId,
        memory_content: memoryContent,
        memory_capacity: memoryCapacity,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener memoria de Zeno del usuario
   */
  async getZenoMemory(userId) {
    try {
      const response = await assistantClient.get(`/zeno-memory/user/${userId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar memoria de Zeno
   */
  async updateZenoMemory(userId, memoryContent) {
    try {
      const response = await assistantClient.put(`/zeno-memory/user/${userId}/content`, {
        memory_content: memoryContent,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar memoria de Zeno
   */
  async deleteZenoMemory(userId) {
    try {
      const response = await assistantClient.delete(`/zeno-memory/user/${userId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Helper para crear contexto de plataforma web
   */
  createWebContext() {
    return {
      platform: "web",
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };
  }

  /**
   * Helper para crear contexto de workflow
   */
  createWorkflowContext(intent, integration = null, additionalData = {}) {
    const workflowContext = {
      intent,
      ...additionalData
    };

    if (integration) {
      workflowContext.integration = integration;
    }

    return workflowContext;
  }

  /**
   * Método conveniencia para iniciar chat nuevo
   */
  async startNewChat(userId, message, workflowIntent = null) {
    const context = this.createWebContext();
    
    const params = {
      userId,
      message,
      context,
      useMemory: true
    };

    // Si hay intención de workflow, agregar contexto
    if (workflowIntent) {
      params.workflowContext = this.createWorkflowContext(workflowIntent);
    }

    return this.chatWithZeno(params);
  }

  /**
   * Método conveniencia para continuar chat existente
   */
  async continueChat(userId, message, chatHistoryId, workflowContext = null) {
    const context = this.createWebContext();
    
    return this.chatWithZeno({
      userId,
      message,
      chatHistoryId,
      context,
      workflowContext,
      useMemory: true
    });
  }
}

export default new ZenoService();
